import React, { useContext, useState } from 'react';
import { styled } from 'styled-components';
import Text from '@ingka/text';
import Button from '@ingka/button';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import AppFooter from '../components/AppFooter';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import RetryMessage from '../components/RetryMessage';
import { showError } from '../reducers/error/errorReducer';
import { isStoreService } from '../helpers/appHelper';
import {
  addPersistingQueryParamsToPath,
  getPath,
  getPrevPath,
} from '../helpers/urlHelper';
import { useStateDispatch, useStateSelector } from '../store/';
import { updateLoadingState } from '../reducers/app/appReducer';
import { getStores } from '../reducers/stores/storesReducer';
import StickyFooter from '../components/StickyFooter';
import { StyledWrapper } from '../components/PageLayout';
import {
  selectAppOperation,
  selectSelectedOriginalService,
} from '../reducers/selectors';
import ExtendedFooterText from '../components/ExtendedFooterText';
import ExtendedHeaderText from '../components/ExtendedHeaderText';
import ListOfServices from '../components/ListOfServices';
import { getServices } from '../reducers/services/servicesReducer';
import { ViewContext } from '../context';

const StyledHeading = styled(Text)`
  &&& {
    margin-bottom: 1rem;
  }
`;

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appOperation = useStateSelector(selectAppOperation);
  const view = useContext(ViewContext);
  const dispatch = useStateDispatch();
  const [isBusy, setIsBusy] = useState(false);
  const searchPostCode = useStateSelector((state) => state.search.postCode);
  const selectedServiceId = useStateSelector(
    (state) => state.app.selected.serviceId,
  );
  const selectedService = useStateSelector(selectSelectedOriginalService);
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );
  const showRetry = useStateSelector((state) => state.services.showRetry);
  const stateCode = useStateSelector((state) => state.search.state || '');
  const availableServices = useStateSelector(
    (state) => state.services.availableServices,
  );
  const customerType = useStateSelector((state) => state.user.customerType);
  const allowedMemberDiscounts = useStateSelector(
    (state) => state.user.allowedMemberDiscounts,
  );

  const isViewAlt = view === 'alt';
  const isViewApp = view === 'app';

  const handleSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    const path = getPath(appOperation, 'timeslots', view);

    try {
      setIsBusy(true);
      if (selectedService && isStoreService(selectedService.location)) {
        const errorActionPayload = {
          title: t('components.error.stores.title'),
          description: t('components.error.stores.description'),
          button: t('components.error.close'),
        };
        try {
          const storeResult = await dispatch(
            getStores({
              serviceProductId: selectedServiceId,
              serviceLocation: selectedService.location,
            }),
          ).unwrap();

          if (storeResult) {
            await navigate(addPersistingQueryParamsToPath(path));
          } else {
            dispatch(showError(errorActionPayload));
            setIsBusy(false);
          }
        } catch (err) {
          dispatch(showError(errorActionPayload));
          setIsBusy(false);
        }
      } else {
        await navigate(addPersistingQueryParamsToPath(path));
      }
      dispatch(updateLoadingState(true));
    } catch (error) {
      setIsBusy(false);
    }
  };

  const previousButtonIsVisible = disabledPrevButton !== 'services';
  const nextButtonIsDisabled = !availableServices.length || !selectedServiceId;
  return (
    <>
      <PageTitle title={t('pages.services.pageTitle')} />
      <form onSubmit={handleSubmit}>
        <StyledWrapper $withStickyFooter={isViewAlt || isViewApp}>
          <PageContent>
            <StyledHeading tagName="h2" textStyle="Heading.L">
              {t('pages.services.title')}
            </StyledHeading>
            <ExtendedHeaderText />
            {showRetry && (
              <RetryMessage
                message={t('pages.services.errorMessage')}
                onRetry={() =>
                  dispatch(
                    getServices({
                      customerType,
                      allowedMemberDiscounts,
                      zipCode: searchPostCode,
                      operation: appOperation,
                      stateCode,
                    }),
                  )
                }
              />
            )}
            <ListOfServices services={availableServices} isBusy={isBusy} />
            {isViewAlt || isViewApp ? (
              <StickyFooter>
                {isViewApp && previousButtonIsVisible && (
                  <Button
                    disabled={isBusy}
                    onClick={() =>
                      navigate(
                        addPersistingQueryParamsToPath(
                          getPrevPath(appOperation, view),
                        ),
                      )
                    }
                    text={t('components.stickyFooter.previousButton')}
                    type="secondary"
                    data-cy="previous"
                    aria-label={t(
                      'components.stickyFooter.ariaLabelBackToStart',
                    )}
                  />
                )}
                <Button
                  htmlType="submit"
                  disabled={nextButtonIsDisabled}
                  loading={isBusy}
                  text={t('components.stickyFooter.nextButton')}
                  type="emphasised"
                  id="nextButton"
                  aria-label={t('components.stickyFooter.ariaLabelToTimeslots')}
                  data-cy="next-button"
                />
              </StickyFooter>
            ) : (
              <AppFooter
                prevVisible={previousButtonIsVisible}
                nextVisible
                isLoading={isBusy}
                nextDisabled={nextButtonIsDisabled}
                nextAriaLabel={t(
                  'components.stickyFooter.ariaLabelToTimeslots',
                )}
                prevAriaLabel={t(
                  'components.stickyFooter.ariaLabelBackToStart',
                )}
              />
            )}
            {!isViewAlt && !isViewApp && <ExtendedFooterText />}
          </PageContent>
        </StyledWrapper>
      </form>
    </>
  );
};

export default ServicesPage;
